import React, { useEffect, useMemo, useState } from "react";
import useApi from "../../CustomHooks/useApi";
import { useToast } from "../../../Contexts/ToastContext";
import { financeAPI } from "../../../api/apiClient";
import Loading from "../../../Loading.js";
import FullRefundReservationsSection from "./FullRefundReservationsSection";
import DraftParticipantAdjustmentsSection from "./DraftParticipantAdjustmentsSection";
import DraftVariantExpensesSection from "./DraftVariantExpensesSection";
import "../../../css/Finance/SettlementDraftSection.css";
import FullRefundParticipantsSection from "./FullRefundParticipantsSection.js";
import SettlementProposedReturnsSection from "./SettlementProposedReturnsSection.js";
import SettlementRelatedTransfersSection from "./SettlementRelatedTransfersSection.js";
import DraftParticipantSettingsSection from "./DraftParticipantSettingsSection.js";

const SettlementDraftSection = ({ tripGuid, financialData, onRefreshFinancialData }) => {
  const { callApi, isLoading } = useApi();
  const { showToast, clearToast } = useToast();
  const [draft, setDraft] = useState(null);
  const [settlementRefreshKey, setSettlementRefreshKey] = useState(0);
  const [publicDrafts, setPublicDrafts] = useState([]);
  const [selectedDraftUserId, setSelectedDraftUserId] = useState(null);
  const [isDraftSwitching, setIsDraftSwitching] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [isChangingVisibility, setIsChangingVisibility] = useState(false);

  const canEditDraft = draft?.canEdit !== false;
  const resetSettlementCalculation = () => setSettlementRefreshKey((prev) => prev + 1);

  const fetchDraft = () => callApi(() => financeAPI.getSettlementDraft(tripGuid), (result) => setDraft(result ?? null), () => clearToast());
  const fetchPublicDrafts = () => callApi(() => financeAPI.getPublicSettlementDrafts(tripGuid), (result) => setPublicDrafts(result ?? []), () => clearToast());

  useEffect(() => {
    const loadInitialData = async () => {
      setInitialLoading(true);
      try { await Promise.all([fetchDraft(), fetchPublicDrafts()]); }
      finally { setInitialLoading(false); }
    };
    loadInitialData();
  }, [tripGuid]);

  const groupedReservations = useMemo(() => {
    const result = {};
    (draft?.variants ?? []).forEach((variant) => { result[variant.variantGuid] = { variantGuid: variant.variantGuid, variantName: variant.variantName, reservations: [] }; });
    (financialData?.reservations ?? []).forEach((reservation) => {
      const key = reservation.variantGuid;
      if (!result[key]) result[key] = { variantGuid: reservation.variantGuid, variantName: reservation.variantName, reservations: [] };
      result[key].reservations.push(reservation);
    });
    return result;
  }, [draft, financialData]);

  if ((isLoading && !draft) || isDraftSwitching || initialLoading) return <Loading text="Ładowanie szkicu rozliczenia..." />;

  const handleToggleVisibility = () => {
    if (isChangingVisibility || !draft?.isMine) return;
    setIsChangingVisibility(true);
    callApi(
      () => financeAPI.setSettlementDraftVisibility(tripGuid, !draft?.isPublic),
      (result) => {
        setDraft(result);
        showToast(result.isPublic ? "Szkic jest teraz publiczny" : "Szkic jest teraz prywatny", "success");
        fetchPublicDrafts();
        setIsChangingVisibility(false);
      },
      () => { clearToast(); setIsChangingVisibility(false); }
    );
  };

  const handleDraftUserChange = (e) => {
    const value = e.target.value;
    setIsDraftSwitching(true);
    resetSettlementCalculation();
    if (value === "mine") {
      setSelectedDraftUserId(null);
      callApi(() => financeAPI.getSettlementDraft(tripGuid), (result) => { setDraft(result ?? null); setIsDraftSwitching(false); }, () => { clearToast(); setIsDraftSwitching(false); });
      return;
    }
    const userId = Number(value);
    setSelectedDraftUserId(userId);
    callApi(() => financeAPI.getSettlementDraftByUser(tripGuid, userId), (result) => { setDraft(result ?? null); setIsDraftSwitching(false); }, () => { clearToast(); setIsDraftSwitching(false); });
  };

  const handleParticipantSettingsChanged = async () => { await fetchDraft(); resetSettlementCalculation(); };

  return (
    <div className="settlement-draft-section">
      <div className="draft-header-box">
        <div className="draft-header-top-row">
          <div className="draft-header-left">
            <div className="draft-header-title-row">
              <div className="draft-header-title">Szkic rozliczenia</div>
              <select className="draft-owner-select" value={selectedDraftUserId ?? "mine"} onChange={handleDraftUserChange}>
                <option value="mine">Mój szkic</option>
                {publicDrafts.map((item) => <option key={item.createdByUserId} value={item.createdByUserId}>{item.createdByName}</option>)}
              </select>
            </div>
            <div className="draft-header-subtitle">Ten widok ma charakter pomocniczy. Excel pozostaje głównym miejscem rozliczenia.</div>
          </div>
          {draft?.isMine && (
            <div className={`draft-visibility-toggle ${draft.isPublic ? "is-public" : ""} ${isChangingVisibility ? "is-loading" : ""}`} onClick={handleToggleVisibility}>
              {isChangingVisibility ? <i className="fa-solid fa-spinner fa-spin" /> : <i className={`fa-solid ${draft.isPublic ? "fa-lock-open" : "fa-lock"}`} />}
              <span>{isChangingVisibility ? "Zmienianie..." : draft.isPublic ? "Publiczny" : "Prywatny"}</span>
            </div>
          )}
        </div>
      </div>

      {draft && !canEditDraft && <div className="draft-readonly-banner"><i className="fa-solid fa-eye" /> Oglądasz {draft.isPublic ? "publiczny" : "prywatny"} szkic osoby: <strong>{draft.createdByName}</strong>.</div>}

      <div className="draft-top-grid">
        <FullRefundReservationsSection tripGuid={tripGuid} draft={draft} groupedReservations={groupedReservations} onDraftChanged={fetchDraft} showToast={showToast} clearToast={clearToast} canEdit={canEditDraft} />
        <FullRefundParticipantsSection tripGuid={tripGuid} draft={draft} financialData={financialData} fullRefundReservationGuids={(draft?.reservations ?? []).filter((r) => r.isFullRefund).map((r) => r.reservationGuid)} onDraftChanged={fetchDraft} showToast={showToast} clearToast={clearToast} canEdit={canEditDraft} />
      </div>

      <div className="draft-top-grid">
        <DraftParticipantAdjustmentsSection tripGuid={tripGuid} draft={draft} financialData={financialData} onDraftChanged={fetchDraft} showToast={showToast} clearToast={clearToast} canEdit={canEditDraft} />
        <DraftVariantExpensesSection tripGuid={tripGuid} draft={draft} groupedReservations={groupedReservations} onDraftChanged={fetchDraft} showToast={showToast} clearToast={clearToast} canEdit={canEditDraft} />
      </div>

      <DraftParticipantSettingsSection tripGuid={tripGuid} draft={draft} financialData={financialData} onChanged={handleParticipantSettingsChanged} showToast={showToast} clearToast={clearToast} canEdit={canEditDraft} />

      <SettlementRelatedTransfersSection transfers={financialData?.relatedTransfers ?? []} />
      <SettlementProposedReturnsSection tripGuid={tripGuid} financeAPI={financeAPI} refreshKey={settlementRefreshKey} draft={draft} />
    </div>
  );
};

export default SettlementDraftSection;
